import { Graphics } from '../../../vendor/pixi.min.mjs';
import { BaseEntity } from '../../entities/BaseEntity.js';
import { FROGGER_CONFIG } from './froggerConfig.js';

const { CELL_SIZE, PLAYER_SIZE, COLS } = FROGGER_CONFIG;
const BALLOON_COLOR = 0xef4444;
const BALLOON_HIGHLIGHT = 0xfca5a5;
const BASKET_COLOR = 0x78350f;

/** Wobble tuning — degrees of sway and frames per cycle. */
const WOBBLE_ANGLE_DEG = 4;
const WOBBLE_PERIOD_FRAMES = 90;

/** Smooth-movement lerp: fraction of remaining distance closed each frame. */
const SMOOTH_LERP = 0.22;
/** Snap to target when remaining gap drops below this (px). */
const SMOOTH_SNAP_EPS = 0.5;

/** Directional lean: max tilt (degrees) when the balloon moves sideways. */
const LEAN_ANGLE_DEG = 14;
/** Fraction of the gap the visible tilt closes toward the target each frame. */
const LEAN_LERP = 0.22;
/** Per-frame decay applied to the lean target (multiplicative).
 *  Lower = lean unwinds faster, so the balloon straightens up quicker. */
const LEAN_DECAY = 0.92;

/**
 * Frogger player — grid-based movement in an infinitely scrolling world.
 * worldRow increases as the player moves UP.
 */
export class FroggerPlayer extends BaseEntity {
    gridCol = 0;
    worldRow = 0;

    /** Pixel X offset of the grid on screen */
    originX = 0;

    _themeManager = null;

    /** Sprite that wobbles each frame (anchored at bottom-center). */
    _wobbleSprite = null;
    _wobbleTime = 0;

    /** Current rendered lean angle (radians). Lerps toward `_leanTarget`. */
    _leanAngle = 0;
    /** Desired lean angle (radians). Set on lateral moves, decays toward 0. */
    _leanTarget = 0;

    /** When true, the rendered position lerps toward the logical cell each
     * frame instead of snapping. Logical `x/y` and `gridCol/worldRow` still
     * update instantly so collision, scoring and the camera target stay
     * responsive — only the visual catches up smoothly. */
    _smoothMovement = false;

    constructor(col, worldRow, originX, themeManager = null) {
        const size = PLAYER_SIZE;
        super(0, 0, size, size);
        this._themeManager = themeManager;
        this._rebuildVisual();
        this.originX = originX;
        this.setPosition(col, worldRow);
    }

    /**
     * @param {number} dc — column delta
     * @param {number} dr — row delta (positive = up)
     * @returns {boolean}
     */
    move(dc, dr) {
        const nextCol = this.gridCol + dc;
        const nextRow = this.worldRow + dr;
        if (nextCol < 0 || nextCol >= COLS) return false;
        if (nextRow < 0) return false;
        this.setPosition(nextCol, nextRow);
        if (dc !== 0) {
            const dir = dc > 0 ? 1 : -1;
            this._leanTarget = dir * (LEAN_ANGLE_DEG * Math.PI / 180);
        }
        return true;
    }

    setPosition(col, worldRow) {
        this.gridCol = col;
        this.worldRow = worldRow;
        const pad = (CELL_SIZE - PLAYER_SIZE) / 2;
        this.x = this.originX + col * CELL_SIZE + pad;
        this.y = -worldRow * CELL_SIZE + pad;
        if (!this._smoothMovement) this.syncPosition();
    }

    /**
     * Enable / disable visual gliding between cells. When disabled, the
     * balloon snaps to its destination instantly (classic behaviour).
     */
    setSmoothMovement(enabled) {
        this._smoothMovement = !!enabled;
        if (!this._smoothMovement) this.syncPosition();
    }

    _rebuildVisual() {
        this.display.removeChildren();
        this._wobbleSprite = null;

        const visualSize = Math.round(CELL_SIZE * 1.1);
        const sprite = this._themeManager?.createSprite('frogger.player', visualSize, visualSize, {
            anchorX: 0.5,
            anchorY: 1.0,
            fitMode: 'contain',
            scaleMultiplier: 1.0,
        });
        if (sprite) {
            sprite.x = PLAYER_SIZE / 2;
            sprite.y = PLAYER_SIZE;
            this.display.addChild(sprite);
            this._wobbleSprite = sprite;
            return;
        }

        const g = new Graphics();
        this._graphics = g;
        this._createPlaceholder(g);
        this.display.addChild(g);
    }

    /** Per-frame wobble + lean + optional smooth glide. Call once per scene update. */
    update(delta) {
        if (this._smoothMovement) this._tickSmooth(delta);

        // Decay the lean *target* exponentially so the moment input stops,
        // the visible tilt unwinds and the idle wobble takes over again.
        this._leanTarget *= Math.pow(LEAN_DECAY, delta);
        const lk = 1 - Math.pow(1 - LEAN_LERP, delta);
        this._leanAngle += (this._leanTarget - this._leanAngle) * lk;

        if (!this._wobbleSprite) return;
        this._wobbleTime += delta;
        const phase = (this._wobbleTime / WOBBLE_PERIOD_FRAMES) * Math.PI * 2;
        const wobble = (WOBBLE_ANGLE_DEG * Math.PI / 180) * Math.sin(phase);
        this._wobbleSprite.rotation = wobble + this._leanAngle;
    }

    /**
     * Lerp the displayed (drawn) position toward the logical x/y.
     * Each frame we close `SMOOTH_LERP` of the remaining gap, scaled by
     * `delta` so the speed stays steady across framerates. When close
     * enough, we snap to exactly the target to avoid sub-pixel jitter.
     */
    _tickSmooth(delta) {
        const dx = this.x - this.display.x;
        const dy = this.y - this.display.y;
        if (Math.abs(dx) < SMOOTH_SNAP_EPS && Math.abs(dy) < SMOOTH_SNAP_EPS) {
            this.display.x = this.x;
            this.display.y = this.y;
            return;
        }
        const k = 1 - Math.pow(1 - SMOOTH_LERP, delta);
        this.display.x += dx * k;
        this.display.y += dy * k;
    }

    _createPlaceholder(g) {
        const w = PLAYER_SIZE;
        const h = PLAYER_SIZE;
        const cx = w / 2;

        g.ellipse(cx, h * 0.42, w * 0.28, h * 0.34);
        g.fill(BALLOON_COLOR);

        g.ellipse(cx - w * 0.08, h * 0.34, w * 0.08, h * 0.11);
        g.fill(BALLOON_HIGHLIGHT);

        g.rect(cx - w * 0.03, h * 0.68, w * 0.06, h * 0.08);
        g.fill(0xd97706);

        g.moveTo(cx - w * 0.08, h * 0.68);
        g.lineTo(cx - w * 0.16, h * 0.88);
        g.lineTo(cx + w * 0.16, h * 0.88);
        g.lineTo(cx + w * 0.08, h * 0.68);
        g.closePath();
        g.fill(BASKET_COLOR);

        g.moveTo(cx - w * 0.12, h * 0.78);
        g.lineTo(cx - w * 0.06, h * 0.68);
        g.moveTo(cx + w * 0.12, h * 0.78);
        g.lineTo(cx + w * 0.06, h * 0.68);
        g.stroke({ color: 0xf3f4f6, width: 2 });
    }
}
